'use es6';

import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import I18n from 'I18n';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { LockedIcon } from 'foundations-assets';
import { toolbarI18nKey } from '../../constants/toolbar';
import { useDebouncedOnChange } from '../../hooks/useDebouncedOnChange';
import SplitLabelStepperInput from './SplitLabelStepperInput';
import { isPercentage } from '../../utils/table';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const i18nKey = suffix => toolbarI18nKey(`tooltip.dimensions.${suffix}`);
const propTypes = {
  dimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number
  }).isRequired,
  maxDimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number
  }).isRequired,
  pixelWidth: PropTypes.number,
  naturalWidth: PropTypes.number,
  naturalHeight: PropTypes.number,
  onDimensionChange: PropTypes.func.isRequired,
  showDimensionsControls: PropTypes.bool,
  dimensionsDisabledTooltip: PropTypes.string,
  disabled: PropTypes.bool,
  allowUnitDimensions: PropTypes.bool,
  lockedAspectRatio: PropTypes.bool,
  activeElement: PropTypes.exact(Element)
};
function DimensionControlGroup(props) {
  const {
    dimensions = {},
    maxDimensions = {},
    pixelWidth,
    naturalWidth,
    naturalHeight,
    onDimensionChange,
    showDimensionsControls = true,
    dimensionsDisabledTooltip,
    disabled: _disabled,
    allowUnitDimensions,
    lockedAspectRatio = true,
    activeElement
  } = props;
  const {
    width,
    height
  } = dimensions;
  const disabled = !showDimensionsControls || _disabled;
  const tooltipDisabled = !dimensionsDisabledTooltip || showDimensionsControls || _disabled;
  const [aspectRatio, setAspectRatio] = useState(() => {
    if (!lockedAspectRatio) return 1;
    if (naturalWidth && naturalHeight) {
      return naturalWidth / naturalHeight;
    }
    return width / height;
  });
  const lastActiveElement = useRef(activeElement);
  useEffect(() => {
    if (lastActiveElement.current !== activeElement && lockedAspectRatio) {
      let updatedAspectRatio;
      if (naturalWidth && naturalHeight) {
        updatedAspectRatio = naturalWidth / naturalHeight;
      } else {
        updatedAspectRatio = width / height;
      }
      setAspectRatio(updatedAspectRatio);
    }
    lastActiveElement.current = activeElement;
  }, [naturalWidth, naturalHeight, width, height, lockedAspectRatio, activeElement]);
  const _handleDimensionChange = (dimensionType, value) => {
    const cappedValue = Math.min(value, maxDimensions[dimensionType] || value);
    const updatedDimensions = {};
    if (dimensionType === 'width') {
      updatedDimensions.width = cappedValue;
      updatedDimensions.height = lockedAspectRatio ? cappedValue / aspectRatio : height;
    } else {
      updatedDimensions.width = lockedAspectRatio ? cappedValue * aspectRatio : width;
      updatedDimensions.height = cappedValue;
    }
    onDimensionChange(updatedDimensions, dimensionType);
  };
  const [handleDimensionInputChange, handleDimensionStepChange] = useDebouncedOnChange(_handleDimensionChange);

  // Props needed for table dimension width control
  let widthStepperInputProps = {};
  if (allowUnitDimensions) {
    if (isPercentage(width)) {
      widthStepperInputProps = {
        max: 100,
        disabledMaxButtonTooltip: I18n.text(i18nKey('maxPercentageTooltip'))
      };
    } else {
      widthStepperInputProps = {
        min: pixelWidth,
        disabledMinButtonTooltip: I18n.text(i18nKey('minPixelTooltip')),
        disabledMinButtonTooltipProps: {
          placement: 'bottom'
        }
      };
    }
  }
  return /*#__PURE__*/_jsx(Tooltip, {
    title: dimensionsDisabledTooltip,
    placement: "bottom",
    disabled: tooltipDisabled,
    children: /*#__PURE__*/_jsxs(TFlex, {
      align: "center",
      "data-test-id": "dimension-control-group",
      children: [/*#__PURE__*/_jsx(SplitLabelStepperInput, Object.assign({
        tooltip: I18n.text(i18nKey('width')),
        label: "W",
        value: width,
        pixelValue: pixelWidth,
        max: maxDimensions.width,
        disabled: disabled,
        onChange: handleDimensionInputChange.bind(null, 'width'),
        onStep: handleDimensionStepChange.bind(null, 'width'),
        allowUnitDimensions: allowUnitDimensions
      }, widthStepperInputProps)), /*#__PURE__*/_jsx(IconButton, {
        use: "transparent",
        size: "xs",
        disabled: true,
        children: /*#__PURE__*/_jsx(LockedIcon, {})
      }), /*#__PURE__*/_jsx(SplitLabelStepperInput, {
        tooltip: I18n.text(i18nKey('height')),
        label: "H",
        value: height,
        pixelValue: height,
        max: maxDimensions.height,
        disabled: disabled,
        onChange: handleDimensionInputChange.bind(null, 'height'),
        onStep: handleDimensionStepChange.bind(null, 'height'),
        allowUnitDimensions: allowUnitDimensions
      })]
    })
  });
}
DimensionControlGroup.propTypes = propTypes;
DimensionControlGroup.displayName = 'DimensionControlGroup';
export default DimensionControlGroup;